import React from 'react';

interface MobileCardField {
  label: string;
  value: React.ReactNode;
  className?: string;
  /** Highlight the value (bold, larger font) */
  highlight?: boolean;
}

interface MobileCardAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface MobileCardProps {
  /** Main title displayed at top of card */
  title: React.ReactNode;
  /** Optional subtitle under the title */
  subtitle?: React.ReactNode;
  /** Status badge in top right */
  status?: React.ReactNode;
  /** Key-value fields to display */
  fields: MobileCardField[];
  /** Action buttons at bottom */
  actions?: MobileCardAction[];
  /** Click handler for the entire card */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show a chevron indicating clickable */
  showChevron?: boolean;
}

/**
 * MobileCard - A touch-friendly card component for displaying data on mobile
 *
 * Use this as an alternative to tables on mobile screens.
 *
 * @example
 * <MobileCard
 *   title={purchase.supplier}
 *   subtitle={purchase.po_number}
 *   status={<StatusBadge status={purchase.status} />}
 *   fields={[
 *     { label: 'Item', value: purchase.item },
 *     { label: 'Amount', value: formatCurrency(purchase.amount) },
 *     { label: 'Date', value: purchase.date },
 *   ]}
 *   actions={[
 *     { label: 'Edit', onClick: () => handleEdit(purchase), variant: 'primary' },
 *     { label: 'Delete', onClick: () => handleDelete(purchase), variant: 'danger' },
 *   ]}
 * />
 */
const MobileCard: React.FC<MobileCardProps> = ({
  title,
  subtitle,
  status,
  fields,
  actions,
  onClick,
  className = '',
  showChevron = false,
}) => {
  const getActionClasses = (variant: MobileCardAction['variant'] = 'secondary') => {
    const base = 'flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors min-h-[44px] flex items-center justify-center gap-1.5';
    switch (variant) {
      case 'primary':
        return `${base} bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800`;
      case 'danger':
        return `${base} bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30`;
      default:
        return `${base} bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600`;
    }
  };

  const CardWrapper = onClick ? 'button' : 'div';
  const cardProps = onClick
    ? {
        type: 'button' as const,
        onClick,
        className: `mobile-card w-full text-left ${className}`,
      }
    : {
        className: `mobile-card ${className}`,
      };

  return (
    <CardWrapper {...cardProps}>
      {/* Header */}
      <div className="mobile-card-header">
        <div className="flex-1 min-w-0">
          <div className="mobile-card-title truncate">{title}</div>
          {subtitle && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {subtitle}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          {status}
          {showChevron && (
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Body - Fields */}
      {fields.length > 0 && (
        <div className="mobile-card-body">
          {fields.map((field, index) => (
            <div key={index} className={`mobile-card-row ${field.className || ''}`}>
              <span className="mobile-card-label">{field.label}</span>
              <span className={`mobile-card-value truncate ml-2 ${field.highlight ? 'font-semibold text-gray-900 dark:text-white' : ''}`}>
                {field.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {actions && actions.length > 0 && (
        <div className="mobile-card-actions">
          {actions.map((action, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              disabled={action.disabled}
              className={getActionClasses(action.variant)}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </CardWrapper>
  );
};

export default MobileCard;
